import { useMemo } from 'react'
import { useTechnicalDay } from './useTechnicalDay'
import { TelescopeDataStatus, useTelescopeDataStatus } from './useTelescopeDataStatus'
import { selectFluxIsLive } from '../../../../../store/fluxLiveSlice'
import { useAppSelector } from '../../../../../store/hooks'
import type { DataStatus } from '../components/DataStatusIndicator'

/** Status texts written by the telescope to rosie.status on rudawy. */
export const TELESCOPE_STATUS = {
  SUN: 'Sun Dataflow',
  RFI: 'RFI Dataflow',
  CORRECTION: 'Correction measurements',
} as const

export interface FluxStatus {
  status: DataStatus
  /** Badge text for statuses without a fixed label (unknown telescope status). */
  text?: string
  /** Tooltip naming the telescope status and since when it holds. */
  title?: string
}

interface DataStatuses {
  flux: FluxStatus
  burstStatus: DataStatus
}

const DATA_FLOWING_STATUS: DataStatus = 'data-flowing'

const isStatus = (actual: string, expected: string) => actual.trim().toLowerCase() === expected.toLowerCase()

const formatSinceUtc = (since: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${since.getUTCFullYear()}-${pad(since.getUTCMonth() + 1)}-${pad(since.getUTCDate())} ` +
    `${pad(since.getUTCHours())}:${pad(since.getUTCMinutes())} UTC`
}

/**
 * Chooses the flux badge. Priority: technical day, then the status reported by the telescope,
 * then whether flux data is arriving (used while no telescope status has been recorded).
 * "Sun Dataflow" without arriving data is shown as a warning, because the data is lost somewhere
 * between the telescope and the page.
 */
export function resolveFluxStatus(
  isTechnicalDay: boolean,
  telescope: TelescopeDataStatus | null,
  isFluxLive: boolean,
): FluxStatus {
  if (isTechnicalDay) {
    return { status: 'technical-day' }
  }
  if (!telescope) {
    return { status: isFluxLive ? DATA_FLOWING_STATUS : 'no-observation' }
  }

  const title = `Telescope status: ${telescope.status.trim()} since ${formatSinceUtc(telescope.since)}`
  if (isStatus(telescope.status, TELESCOPE_STATUS.SUN)) {
    return { status: isFluxLive ? DATA_FLOWING_STATUS : 'no-data-received', title }
  }
  if (isStatus(telescope.status, TELESCOPE_STATUS.RFI)) {
    return { status: 'rfi-measurements', title }
  }
  if (isStatus(telescope.status, TELESCOPE_STATUS.CORRECTION)) {
    return { status: 'correction-measurements', title }
  }
  return { status: 'unknown', text: telescope.status.trim(), title }
}

/**
 * Custom hook to determine the current data status for flux and burst indicators.
 * Technical day status has the highest priority and overrides all other states.
 *
 * @param isBurstActive - Local burst status from WebSocket (true if burst is active)
 * @returns Flux badge (status, optional text and tooltip) and burstStatus
 */
export function useDataStatus(isBurstActive: boolean): DataStatuses {
  const isFluxLive = useAppSelector(selectFluxIsLive)
  const isTechnicalDay = useTechnicalDay()
  const telescope = useTelescopeDataStatus()

  return useMemo(() => {
    let burstStatus: DataStatus = isBurstActive ? DATA_FLOWING_STATUS : 'no-observation'
    if (isTechnicalDay) {
      burstStatus = 'technical-day'
    }
    return {
      flux: resolveFluxStatus(isTechnicalDay, telescope, isFluxLive),
      burstStatus,
    }
  }, [isFluxLive, isBurstActive, isTechnicalDay, telescope])
}
